import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Logo from '../components/Logo';
import SEO from '../components/SEO';

const FAQ = () => {
  const [openIndex, setOpenIndex] = React.useState(null);

  const faqSections = [
    {
      title: 'About id100',
      faqs: [
        {
          question: 'What is id100?',
          answer: 'id100 is an AI-powered due diligence platform built by Totalinfo Ltd. It produces professional-grade due diligence reports on companies, individuals, or business counterparties. The platform uses multiple specialised AI agents to research, analyse, and write comprehensive reports that would traditionally take a law firm or consulting firm days or weeks to produce.'
        },
        {
          question: 'Who is this service for?',
          answer: 'id100 is designed for anyone who needs to assess the risk of doing business with a company or individual. This includes: solicitors and law firms conducting client due diligence; accountants and auditors assessing counterparty risk; investors evaluating potential deals or acquisitions; procurement teams vetting new suppliers; compliance officers screening business relationships; private equity and venture capital firms; and anyone entering into a significant business transaction who wants to understand who they are dealing with.'
        },
        {
          question: 'Why should I use id100 instead of doing my own research?',
          answer: 'Traditional due diligence is slow, expensive, and often incomplete. A manual background check might take 2-4 weeks and cost thousands of pounds. id100 reviews 100% of publicly available information across corporate registries, court records, sanctions lists, news archives, regulatory databases, and more, delivering a structured professional report in minutes. You get broader coverage, faster turnaround, and consistent quality at a fraction of the cost.'
        },
        {
          question: 'What does "id100" mean?',
          answer: 'id100 stands for "identification of 100% of the publicly available data." Unlike traditional due diligence where researchers check a handful of sources, our AI agents systematically scan all accessible public information to build the most complete picture possible.'
        },
      ]
    },
    {
      title: 'Reports & Outputs',
      faqs: [
        {
          question: 'What is included in a due diligence report?',
          answer: 'Each report contains 13 professionally structured sections covering: executive summary, entity verification and corporate structure, ownership and control analysis, financial health assessment, legal and regulatory review, sanctions and PEP screening, reputational analysis, operational capability, jurisdictional risk factors, adverse media screening, related party analysis, risk scoring across six categories (financial, legal, operational, reputational, jurisdictional, security), and a professional recommendation. Every finding is supported by source citations.'
        },
        {
          question: 'How can I use the report outputs?',
          answer: 'Reports are downloaded as professional Word (.docx) or PDF documents. You can use them to: support Know Your Customer (KYC) and Anti-Money Laundering (AML) compliance; present to boards, committees, or legal counsel for decision-making; include in transaction files as part of your due diligence record; attach to loan applications or investment memoranda; share with insurers as part of risk assessment; and file as evidence of reasonable enquiries in regulatory contexts. The reports are formatted to the standard expected by professional services firms and regulators.'
        },
        {
          question: 'How long does it take to generate a report?',
          answer: 'Report generation typically takes around one hour, though this can vary depending on the complexity of the subject and the volume of publicly available information. Our multi-agent pipeline runs multiple research phases — some in parallel — followed by professional report writing, quality checking, and a final orchestration review. This thoroughness is what ensures the depth and quality of the output. You will receive an email notification once your report is ready, and you can monitor progress from your dashboard.'
        },
        {
          question: 'What is the risk score and how is it calculated?',
          answer: 'Each report includes a composite risk score from 1 to 10 across six weighted categories: Financial Risk (25%), Legal/Compliance Risk (20%), Operational Risk (20%), Reputational Risk (15%), Jurisdictional Risk (10%), and Security Risk (10%). The scoring follows the Enhanced Due Diligence Framework V3 methodology. Scores of 1-2.5 indicate low risk, 2.6-5.5 moderate risk, 5.6-8.5 high risk, and 8.6-10 extreme risk.'
        },
        {
          question: 'Can I export my reports?',
          answer: 'Yes. Once your report is complete, you can download it in Word (.docx) or PDF format. Word format is useful if you want to edit or annotate the report before sharing. PDF format provides a polished, print-ready document with a branded cover page, risk dashboard, table of contents, and source appendix.'
        },
        {
          question: 'Are the reports legally admissible?',
          answer: 'Reports are provided for informational purposes and do not constitute legal advice. However, they are structured to the professional standard expected in commercial due diligence and can form part of a broader due diligence process. Many users attach them to transaction files as evidence of reasonable enquiries. For legal proceedings, we recommend having the findings reviewed by qualified legal counsel.'
        },
        {
          question: 'How accurate are the reports?',
          answer: 'Our AI agents draw upon a wide array of publicly accessible data sets and global information resources — corporate registries, court records, regulatory databases, news archives, sanctions lists, and more. The reports represent a thorough synthesis of what the public record contains at the time of generation. However, they may contain errors, inaccuracies, or omissions arising from the limitations of AI technology, gaps in publicly available information, or the imprecision inherent in open source research. We make no warranty as to the accuracy, completeness, or fitness for any particular purpose of any report. You should treat findings as a starting point for further enquiry and verify anything material independently.'
        },
        {
          question: 'Can I use a report to make decisions about a person — such as employment or credit?',
          answer: 'No. Reports must not be used as the sole or primary basis for decisions that could have a material legal or practical consequence for an identifiable individual or entity. This includes decisions relating to employment, creditworthiness, housing, insurance, healthcare, education, regulatory action, or access to services. Such decisions must be reached through your own independent assessment and, where appropriate, with qualified professional advice. Our Terms of Service set out these restrictions in full, and by using the platform you agree to them.'
        },
        {
          question: 'What are the limitations of AI-generated due diligence?',
          answer: 'AI-generated due diligence is powerful but not infallible. Limitations include: information that is not publicly accessible cannot be found; records in some jurisdictions may be incomplete or not digitised; names with common spellings can produce results relating to different individuals; very recently filed documents may not yet be indexed; and AI systems can occasionally misinterpret or conflate information. For high-stakes transactions we recommend using our reports as a comprehensive first-pass research tool, supplemented by direct verification of the most material findings.'
        },
      ]
    },
    {
      title: 'How It Works',
      faqs: [
        {
          question: 'How does the AI research work?',
          answer: 'id100 uses a multi-agent pipeline with six specialised AI agents. The Web Research Agent scans broadly across the internet. The Deep Research Agent conducts targeted OSINT investigation into company registries, courts, and regulatory databases. The Advanced Research Agent performs in-depth analytical research on financial patterns, corporate structures, and legal/regulatory signals. All three research outputs are combined and passed to the Arranger Agent, which writes the full professional report. The Checking Agent scores the report for quality, and the Orchestrator Agent performs a final review for coherence and professional tone.'
        },
        {
          question: 'What data sources are accessed?',
          answer: 'Our agents search across: Companies House and international corporate registries; court databases and legal records; global sanctions lists (OFAC, EU, UN, HMT); Politically Exposed Person (PEP) databases; news archives and press releases; regulatory enforcement databases; financial filing repositories; academic and industry publications; social media and digital footprint; patent and intellectual property databases; and business directories and trade associations.'
        },
        {
          question: 'What jurisdictions do you cover?',
          answer: 'We provide global coverage. The AI agents can research entities in any jurisdiction with publicly accessible records. Coverage is strongest in the UK, EU, US, Canada, Australia, and other common law jurisdictions, but extends worldwide including offshore financial centres and emerging markets.'
        },
      ]
    },
    {
      title: 'Pricing & Payment',
      faqs: [
        {
          question: 'How much does a report cost?',
          answer: 'We offer flexible pricing: a single report for a one-off fee, or monthly subscription plans (Essential and Professional) that include multiple reports per month at a reduced per-report cost. Visit our Pricing page for current rates. We also offer promotional vouchers from time to time.'
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit and debit cards (Visa, Mastercard, American Express) through our secure Stripe payment processing. Payment is required before report generation begins.'
        },
        {
          question: 'Can I get a refund if I\'m not satisfied?',
          answer: 'Due to the extensive AI processing and research involved in generating each report, we do not offer refunds once a report has been generated. However, we stand behind the quality of our work. If you believe your report contains errors or is significantly incomplete, please contact our support team and we will review it.'
        },
        {
          question: 'Do you offer enterprise or bulk pricing?',
          answer: 'Yes. For organisations requiring high-volume due diligence services, please contact us to discuss custom enterprise solutions with dedicated support, bulk pricing, and potential API access.'
        },
      ]
    },
    {
      title: 'Account & Security',
      faqs: [
        {
          question: 'How do I get started?',
          answer: 'Click "Get Started" on the homepage, create an account with your email and a password, verify your email address by clicking the link we send you, then you can purchase a report or subscribe to a plan and start generating due diligence reports immediately.'
        },
        {
          question: 'How do I verify my email address?',
          answer: 'After registration, you will receive a verification email. Click the link in the email to verify your account. If you didn\'t receive the email, check your spam/junk folder or click the "Verify Email Now" button on your dashboard to resend it. Email verification is required before you can generate reports.'
        },
        {
          question: 'Is my data secure?',
          answer: 'Yes. All data is transmitted securely using SSL/TLS encryption. We comply with GDPR and UK data protection regulations. Your reports are confidential and only accessible to you through your authenticated account. We do not share your data with third parties or use it for AI training. API keys and sensitive credentials are encrypted using AES-256-CBC.'
        },
        {
          question: 'Can other people see my reports?',
          answer: 'No. Reports are tied to your account and are only visible to you when logged in. Even our admin team accesses reports only for quality assurance and support purposes. Reports are never shared publicly or with other users.'
        },
      ]
    },
    {
      title: 'Support',
      faqs: [
        {
          question: 'What if I need help or have questions?',
          answer: 'You can reach our support team via the Contact Us page or by emailing info@totalinfo.net. We aim to respond to all inquiries within 24 hours during business days.'
        },
        {
          question: 'I\'m having trouble logging in. What should I do?',
          answer: 'First, ensure you are using the correct email address and password. If you\'ve forgotten your password, click "Forgot your password?" on the sign-in page to reset it. If you continue to have issues, clear your browser cache and cookies, or try a different browser. If the problem persists, contact our support team.'
        },
        {
          question: 'Can I suggest improvements to the platform?',
          answer: 'Absolutely. We welcome feedback from our users. Please use the Contact Us page to share your suggestions. We actively develop new features based on user feedback.'
        },
      ]
    }
  ];

  // Flatten for indexing
  const faqs = faqSections.flatMap(s => s.faqs);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="FAQ — id100"
        description="Frequently asked questions about id100's AI-powered due diligence reports, pricing, data sources, and report quality."
        path="/faq"
      />
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Logo size="md" />
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/pricing"
                className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium"
              >
                Pricing
              </Link>
              <Link
                to="/faq"
                className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium"
              >
                FAQ
              </Link>
              <Link
                to="/contact"
                className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium"
              >
                Contact
              </Link>
              <Link
                to="/auth"
                className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium"
              >
                Sign In
              </Link>
              <Link
                to="/auth"
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-gray-600">
            Find answers to common questions about our due diligence platform
          </p>
        </div>

        <div className="space-y-10">
          {faqSections.map((section, sIdx) => {
            const baseIndex = faqSections.slice(0, sIdx).reduce((sum, s) => sum + s.faqs.length, 0);
            return (
              <div key={sIdx}>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-red-600 pb-2">
                  {section.title}
                </h2>
                <div className="space-y-3">
                  {section.faqs.map((faq, fIdx) => {
                    const globalIndex = baseIndex + fIdx;
                    return (
                      <div
                        key={globalIndex}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <button
                          onClick={() => setOpenIndex(openIndex === globalIndex ? null : globalIndex)}
                          className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors flex justify-between items-center"
                        >
                          <span className="text-lg font-medium text-gray-900">
                            {faq.question}
                          </span>
                          {openIndex === globalIndex ? (
                            <ChevronUp className="h-5 w-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-500" />
                          )}
                        </button>
                        {openIndex === globalIndex && (
                          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                            <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 bg-gray-50 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Still have questions?
          </h2>
          <p className="text-gray-600 mb-6">
            Can't find the answer you're looking for? Please get in touch with our team.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-md font-medium transition-colors"
          >
            Contact Support
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="mb-4">
                <Logo size="sm" />
              </div>
              <p className="text-sm text-gray-400">
                Professional due diligence reports powered by AI technology.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link></li>
                <li>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new Event('id100:open-cookie-prefs'))}
                    className="hover:text-white transition-colors text-left bg-transparent border-0 p-0 cursor-pointer"
                  >Cookie preferences</button>
                </li>
                <li><Link to="/data-request" className="hover:text-white transition-colors">Data subject request</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="mailto:info@totalinfo.net" className="hover:text-white transition-colors">
                    info@totalinfo.net
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Totalinfo Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
